"use client";

import { useEffect } from "react";
import { BELOW_FOLD_ATTRIBUTE, renderBelowFoldBand, renderBelowFoldBands } from "./below-fold";

/**
 * `SITE-CRS02`: keeps a page's in-page jumps exact while its bands below the
 * first screen skip layout (`BELOW_FOLD_BAND`). After the load event it renders
 * those bands one per idle period, so each keeps its real height from then on;
 * a click on a same-page `#` link, or a URL that arrives with a hash, renders
 * the rest at once first. Scripted jumps call `renderBelowFoldBands` themselves.
 * Renders nothing.
 */
export function BelowFoldBands() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const link = event.target instanceof Element ? event.target.closest("a[href*='#']") : null;
      if (link instanceof HTMLAnchorElement && link.hash && link.pathname === window.location.pathname) {
        renderBelowFoldBands();
      }
    };
    document.addEventListener("click", onClick, true);

    const hashTarget = window.location.hash
      ? document.getElementById(decodeURIComponent(window.location.hash.slice(1)))
      : null;
    if (hashTarget) {
      renderBelowFoldBands();
      hashTarget.scrollIntoView({ block: "start" });
    }

    let cancelled = false;
    let idle: number | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const step = () => {
      if (cancelled) return;
      const next = document.querySelector<HTMLElement>(`[${BELOW_FOLD_ATTRIBUTE}=""]`);
      if (!next) return;
      renderBelowFoldBand(next);
      schedule();
    };
    const schedule = () => {
      if ("requestIdleCallback" in window) idle = window.requestIdleCallback(step);
      else timer = setTimeout(step, 200);
    };
    const start = () => schedule();
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });

    return () => {
      cancelled = true;
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("load", start);
      if (idle !== null) window.cancelIdleCallback(idle);
      if (timer !== null) clearTimeout(timer);
    };
  }, []);

  return null;
}
